/**
 * t518u: Channel settings → Posting → Journeys. Any channel form can create a journey from each submission. The
 * admin picks the start building, when journeys leave, and which form fields give the destination, date, window,
 * load and notes. Edits are kept until the dialog's Save, like the rest of Channel settings.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  JOURNEY_LINK_SLOTS, START_TIME_RE, suggestJourneyLinkFields, validateJourneyLinkFields,
  type FormField, type FormJourneyLinkInput, type JourneyLinkFields,
} from '@tradeoutlet/shared';
import { api } from '@/lib/api';
import { queryClient } from '@/lib/queryClient';
import { BuildingSelect } from '@/features/buildings/common';
import { Field, Input, Select, Toggle } from '@/components/ui';

type Draft = { enabled: boolean; origin_location_id: string | null; start_time: string; fields: JourneyLinkFields };

export function useFormJourneyLink(ws: string | null | undefined, conversationId: string, templateFields: FormField[] | null) {
  const q = useQuery({
    queryKey: ['form-journey-link', conversationId], enabled: Boolean(ws && templateFields), retry: false,
    queryFn: () => api.fleet.formLink(ws!, conversationId),
  });
  const saved = useMemo<Draft | null>(() => (q.data ? (q.data.link
    ? { enabled: q.data.link.enabled, origin_location_id: q.data.link.origin_location_id, start_time: q.data.link.start_time, fields: q.data.link.fields }
    : { enabled: false, origin_location_id: null, start_time: '08:00', fields: {} }) : null), [q.data]);
  const [draft, setDraft] = useState<Draft | null>(null);
  useEffect(() => { if (saved && !draft) setDraft(saved); }, [saved, draft]);
  const dirty = Boolean(q.data?.can_manage && draft && saved && JSON.stringify(draft) !== JSON.stringify(saved));
  const problem = useMemo(() => {
    if (!dirty || !draft || !templateFields) return null;
    if (!draft.enabled && !q.data?.link) return null;
    if (!draft.origin_location_id) return 'Choose the start building';
    if (!START_TIME_RE.test(draft.start_time)) return 'Choose when journeys leave';
    return validateJourneyLinkFields(templateFields, draft.fields)[0] ?? null;
  }, [dirty, draft, templateFields, q.data?.link]);
  const save = async () => {
    if (!draft || !ws || !draft.origin_location_id) return;
    const body: FormJourneyLinkInput = { enabled: draft.enabled, origin_location_id: draft.origin_location_id, start_time: draft.start_time, fields: draft.fields };
    const r = await api.fleet.saveFormLink(ws, conversationId, body);
    queryClient.setQueryData(['form-journey-link', conversationId], r);
    setDraft(null);
    void queryClient.invalidateQueries({ queryKey: ['channel-form', conversationId] });
  };
  // Nothing to save when it was never switched on and still isn't.
  const needsSave = dirty && Boolean(draft && (draft.enabled || q.data?.link));
  return { info: q.data, draft, setDraft, dirty: needsSave, problem, save };
}

export function FormJourneyLinkFields({ state, fields }: { state: ReturnType<typeof useFormJourneyLink>; fields: FormField[] }) {
  const { info, draft, setDraft } = state;
  if (!info || !draft) return null;
  const ro = !info.can_manage;
  const set = (patch: Partial<Draft>) => setDraft({ ...draft, ...patch });
  const toggle = (on: boolean) => {
    if (ro) return;
    // First time on: guess the mapping from the form's field types and names.
    set({ enabled: on, fields: on && !Object.keys(draft.fields).length ? suggestJourneyLinkFields(fields) : draft.fields });
  };
  return (
    <div className="space-y-3 rounded-lg border border-slate-200 p-3 dark:border-slate-700" data-testid="form-journey-link">
      <Toggle checked={draft.enabled} onChange={toggle} label="Create a journey from each submission" />
      {draft.enabled && <>
        <p className="text-xs text-slate-500">Each new post makes an unassigned journey. Journey updates go in the post’s thread, and the post is marked complete when its stop is done.</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Start building"><BuildingSelect disabled={ro} value={draft.origin_location_id} onChange={v => set({ origin_location_id: v })} placeholder="Choose…" /></Field>
          <Field label="Leaves at"><Input type="time" noHint disabled={ro} value={draft.start_time} onChange={e => set({ start_time: e.target.value })} data-testid="form-journey-start-time" /></Field>
          {JOURNEY_LINK_SLOTS.map(slot => {
            const options = fields.filter(f => slot.types.includes(f.type) && !(f.type === 'building' && f.multiple));
            return (
              <Field key={slot.key} label={slot.required ? slot.label : `${slot.label} (optional)`}>
                <Select disabled={ro} value={draft.fields[slot.key] ?? ''} data-testid={`form-journey-field-${slot.key}`}
                  onChange={e => set({ fields: { ...draft.fields, [slot.key]: e.target.value || null } })}>
                  <option value="">{slot.required ? 'Choose a field…' : 'Not used'}</option>
                  {options.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
                </Select>
              </Field>
            );
          })}
        </div>
        {state.problem && <p className="text-xs text-slate-500" data-testid="form-journey-problem">{state.problem}</p>}
        {ro && <p className="text-xs text-slate-500">Form managers who plan journeys can change this.</p>}
      </>}
    </div>
  );
}
